import React from 'react';
import SoaDisclaimerBar from './SoaDisclaimerBar';
import { SoaDisclaimerMode, SoaDisclaimerStatus } from '@/types/soa';
import styles from './SoaSendModal.module.css';

interface SoaDisclaimerPickerProps {
    mode: SoaDisclaimerMode;
    onModeChange: (mode: SoaDisclaimerMode) => void;
    text: string;
    onTextChange: (text: string) => void;
    disclaimerStatus: SoaDisclaimerStatus | null;
    onDisclaimerStatusChange: (status: SoaDisclaimerStatus) => void;
    disabled?: boolean;
}

/**
 * Shared by the send modal and the per-document download dialog, so both show the
 * exact same disclaimer content and toggle — the download exists specifically to
 * preview what a send would produce.
 */
const SoaDisclaimerPicker: React.FC<SoaDisclaimerPickerProps> = ({
    mode, onModeChange, text, onTextChange, disclaimerStatus, onDisclaimerStatusChange, disabled,
}) => {
    const usingDefault = !!disclaimerStatus
        && (mode === 'text' ? !disclaimerStatus.has_custom_text : !disclaimerStatus.exists);

    return (
        <div className={styles.field}>
            <label>
                Disclaimer<span className={styles.required}>*</span>
            </label>

            <div className={styles.typeOptions} style={{ marginBottom: 10 }}>
                <label className={`${styles.typeCard} ${mode === 'text' ? styles.typeCardSelected : ''}`}>
                    <span className={styles.typeCardHead}>
                        <input
                            type="radio"
                            name="soa-disclaimer-mode"
                            checked={mode === 'text'}
                            disabled={disabled}
                            onChange={() => onModeChange('text')}
                        />
                        Text
                    </span>
                    <span className={styles.typeCardSub}>Edit the wording, including the two dates</span>
                </label>
                <label className={`${styles.typeCard} ${mode === 'pdf' ? styles.typeCardSelected : ''}`}>
                    <span className={styles.typeCardHead}>
                        <input
                            type="radio"
                            name="soa-disclaimer-mode"
                            checked={mode === 'pdf'}
                            disabled={disabled}
                            onChange={() => onModeChange('pdf')}
                        />
                        PDF
                    </span>
                    <span className={styles.typeCardSub}>Upload a ready-made disclaimer file</span>
                </label>
            </div>

            {usingDefault && (
                <div className={`${styles.banner} ${styles.bannerWarning}`} style={{ marginBottom: 10 }}>
                    <span>
                        No custom disclaimer text is saved and no PDF is uploaded — the
                        built-in default text will be used. Review it below before continuing.
                    </span>
                </div>
            )}

            {mode === 'text' ? (
                <textarea
                    className={styles.input}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', resize: 'vertical' }}
                    rows={10}
                    value={text}
                    disabled={disabled}
                    onChange={event => onTextChange(event.target.value)}
                />
            ) : (
                <SoaDisclaimerBar variant="row" onStatusChange={onDisclaimerStatusChange} />
            )}
        </div>
    );
};

export default SoaDisclaimerPicker;
